import React from 'react';
import { USER_ROLES } from 'src/common/constants';
import Button from 'src/components/Button';
import { Hidden } from 'src/components/Table';
import FontAwesomeIcon from 'src/components/Icon';
import Tooltip from 'src/components/Tooltip/Tooltip';
import WalkthroughStatusView from 'src/components/WalkThroughTable/Components/WalkthroughStatusView';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import CalcomScheduledBadge from 'src/pages/Calendar/CalcomIntegration/components/CalcomScheduledBadge/CalcomScheduledBadge';
import { IWalkthroughRow } from 'src/pages/Suite/CustomHooks/useSuitWalkthroughs';

const ACTIVATE_HINT: string =
  'To activate, use Activate Walkthroughs from the property’s Suites view.';

// Activation moved to the property-level bulk tool — the per-row status now shows as plaintext
// with a pointer to where activation actually happens, instead of an inline activate button.
const ActivatePendingStatus = () => (
  <span className="walkthrough-activate-pending">
    Yet to be activated
    <Tooltip content={ACTIVATE_HINT}>
      <FontAwesomeIcon glyph="info-circle" weight="light" size="small" />
    </Tooltip>
  </span>
);

export interface GenerateWalkthroughColumnsArgs {
  user: any;
  handleStatusClicked: (data: any) => void;
  exportPDF: (data: any) => void;
  onCheckUserCanAccessTheModule: (roles: string[]) => boolean;
  propertyManagerDetails: any;
  onDelete: (data: any) => void;
  onPreview: (data: any) => void;
}

export const generateWalkthroughColumns = ({
  user,
  handleStatusClicked,
  exportPDF,
  onCheckUserCanAccessTheModule,
  propertyManagerDetails,
  onDelete,
  onPreview,
}: GenerateWalkthroughColumnsArgs) => [
  {
    label: 'Name',
    accessor: 'walkthroughName',
  },
  {
    label: 'Status',
    width: '200px',
    style: { minWidth: '10rem' },
    cell: (data: IWalkthroughRow) => {
      const isRealtor: boolean = user.roles[0].role.name === 'Realtor';
      const canSeeStatus: boolean =
        !isRealtor || (isRealtor && data.status === 'Review');

      if (!canSeeStatus) {
        return null;
      }

      // A confirmed Cal.com booking overrides the legacy scheduling affordance — show the
      // branded "scheduled" chip (with its own reschedule link) instead of the Schedule button.
      if (data.calcomBookingUid) {
        return <CalcomScheduledBadge calcomBookingUid={data.calcomBookingUid} />;
      }

      if (data.status === 'Activate') {
        return <ActivatePendingStatus />;
      }

      return (
        <div
          className="walkThroughTable__status"
          style={{ display: 'flex', justifyContent: 'left', cursor: 'pointer' }}
          onClick={() => handleStatusClicked(data)}
        >
          <WalkthroughStatusView
            data={data}
            propertyManagerDetails={propertyManagerDetails}
            renderActivate={() => <ActivatePendingStatus />}
          />
        </div>
      );
    },
  },
  {
    label: 'Type',
    accessor: 'walkthroughType',
  },
  {
    label: 'Permissions',
    cell: (data: any) => <div>{data.permissions || 'N/A'}</div>,
  },
  {
    xsHide: true,
    label: 'Description',
    cell: (data: any) => (
      <div style={{ maxWidth: '280px' }}>
        <ExpandableText
          text={data.description || 'No description provided'}
          characterLimit={60}
        />
      </div>
    ),
  },
  {
    center: true,
    label: 'Issues Logged',
    accessor: 'serviceRequestCount',
  },
  {
    center: true,
    label: 'Export to PDF',
    cell: (data: any) =>
      data?.pdfurl ? (
        <a
          onClick={() => exportPDF(data)}
          style={{ color: 'black', cursor: 'pointer', fontWeight: 600 }}
        >
          <FontAwesomeIcon glyph="download" weight="light" /> Export
        </a>
      ) : (
        'N/A'
      ),
  },
  {
    center: true,
    label: 'Preview',
    cell: (data: any) =>
      data.walkthroughType === 'Enhanced' ? (
        <a
          onClick={() => onPreview(data)}
          style={{ cursor: 'pointer', color: '#2f2f2f' }}
        >
          <FontAwesomeIcon glyph="eye" weight="light" />
        </a>
      ) : (
        'N/A'
      ),
  },
  {
    center: true,
    label: 'Completed Date',
    cell: (data: any) =>
      data.completedDate
        ? new Date(data.completedDate).toLocaleDateString()
        : 'N/A',
  },
  {
    cell: (data: any) =>
      onCheckUserCanAccessTheModule([
        USER_ROLES.PROPERTY_DEVELOPER,
        USER_ROLES.ADMIN,
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.STRATA_MANAGER,
      ]) && (
        <Hidden>
          <Button
            color="semantic"
            label={<FontAwesomeIcon glyph="trash" weight="light" />}
            onClick={() => onDelete(data)}
          />
        </Hidden>
      ),
  },
];
